import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import BlurredBackgroundSection from 'components/BlurredBackgroundSection';
import CenteredContainer from 'components/CenteredContainer';
import MainActions from '../MainActions';
import Info from '../Info';
import OrgEventsList from 'components/EventsList/OrgEventsList';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import TeamList from 'components/TeamList';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import OrgDirectory from '../../../associations/components/OrgDirectory';
import PillButton from '../PillButton';
import AssociatedWith from '../AssociatedWith';
import { OutgoingMembershipDto } from 'api/dtos/memberships/products';
import AssocEventsList from 'components/EventsList/AssocEventsList';

interface Props {
  toggleShareModal: () => void;
  join: () => void;
  badgeUrl: string;
  coverUrl: string;
  name: string;
  manageText: string;
  memberCount: number;
  memberPreview: {
    firstName: string;
    id: string;
    lastName: string;
    photoUrl?: string;
  }[];
  address: string;
  editLink?: string;
  membersLink?: string;
  dashboardLink?: string;
  description: string;
  isUserAdmin: boolean;
  isUserMember: boolean;
  isUserPending: boolean;
  organizationId?: string;
  hasOngoingMembership?: boolean;
  upcomingMembership?: OutgoingMembershipDto;
  associationDependentId?: string;
  isJoinBlocked?: boolean;
  blockedMessage?: string;
}

const DesktopView = ({
  toggleShareModal,
  coverUrl,
  name,
  memberCount,
  memberPreview,
  address,
  description,
  isUserAdmin,
  isUserMember,
  isUserPending,
  manageText,
  editLink,
  dashboardLink,
  join,
  badgeUrl,
  membersLink,
  organizationId,
  hasOngoingMembership,
  associationDependentId,
  upcomingMembership,
  isJoinBlocked = false,
  blockedMessage
}: Props) => {
  const { t } = useTranslation(['associations', 'organizations']);
  const flags = useFlags();
  const router = useRouter();
  const { query } = router;
  const isAssociationPage = query.associationId;
  const user = useSelector((state: any) => state.user?.user);
  const [orgActiveTab, setOrgActiveTab] = useState<'events' | 'team'>('events');
  const [assocActiveTab, setAssocActiveTab] = useState<'orgs' | 'events'>(
    'orgs'
  );

  return (
    <>
      <div className="absolute left-0 right-0 top-0 h-44 bg-linear-to-b from-[rgba(0,0,0,0.7)]" />
      <BlurredBackgroundSection imageUrl={coverUrl} backgroundHeight="1300px">
        <CenteredContainer>
          <div className="flex flex-col">
            <div className="flex pt-36 pb-11 flex-row">
              <div className="flex-1 relative">
                <div
                  className="bg-primary-200 w-[318px] rounded-3xl overflow-hidden items-center justify-center flex"
                  style={{
                    aspectRatio: '9 / 16'
                  }}
                >
                  <img
                    src={coverUrl}
                    className="w-full h-full object-cover"
                    alt={`${name}'s cover`}
                  />
                </div>
              </div>
              <div className="flex-1 gap-4 flex items-start flex-col justify-center">
                <Info
                  name={name}
                  memberCount={memberCount}
                  memberPreview={memberPreview}
                  address={address}
                  badgeUrl={badgeUrl}
                  membersLink={membersLink}
                  isUserAdmin={isUserAdmin}
                />
                <MainActions
                  hasOngoingMembership={hasOngoingMembership}
                  toggleShareModal={toggleShareModal}
                  manageText={manageText}
                  isUserAdmin={isUserAdmin}
                  editLink={editLink}
                  dashboardLink={dashboardLink}
                  isUserMember={isUserMember}
                  join={join}
                  isUserPending={isUserPending}
                  upcomingMembership={upcomingMembership}
                  isJoinBlocked={isJoinBlocked}
                  blockedMessage={blockedMessage}
                />
                <div className="text-[0.9375rem] text-[#202020] leading-[18px] tracking-[0.005px] font-normal w-full">
                  {description}
                </div>
                {!isAssociationPage && associationDependentId && (
                  <AssociatedWith associationId={associationDependentId} />
                )}
              </div>
            </div>
            {isAssociationPage && (
              <div className="flex flex-row mx-auto items-center justify-center gap-2">
                <PillButton
                  active={assocActiveTab === 'orgs'}
                  label={t('ourOrgs')}
                  icon="flag"
                  onClick={() => setAssocActiveTab('orgs')}
                />
                <PillButton
                  active={assocActiveTab === 'events'}
                  label={t('publicView.ourEvents')}
                  icon="calendar"
                  onClick={() => setAssocActiveTab('events')}
                />
              </div>
            )}
          </div>
        </CenteredContainer>
      </BlurredBackgroundSection>
      {isAssociationPage && (
        <>
          {assocActiveTab === 'orgs' && (
            <div className="py-6">
              <OrgDirectory associationId={organizationId} user={user} />
            </div>
          )}
          {assocActiveTab === 'events' && (
            <div className="py-6">
              <AssocEventsList associationId={organizationId} user={user} />
            </div>
          )}
        </>
      )}
      {!isAssociationPage &&
        (flags[FLAGS.ENABLE_ORG_EVENTS] ||
          flags[FLAGS.ENABLE_ORGANIZATION_TITLES]) && (
          <CenteredContainer wideContent>
            <div className="w-full flex flex-col gap-2 justify-center text-base font-normal py-4">
              <div className="flex flex-row mx-auto items-center justify-center gap-4">
                {flags[FLAGS.ENABLE_ORG_EVENTS] && (
                  <PillButton
                    active={orgActiveTab === 'events'}
                    onClick={() => setOrgActiveTab('events')}
                    label={t('organizations:publicView.ourEvents')}
                    icon="calendar"
                  />
                )}
                {flags[FLAGS.ENABLE_ORGANIZATION_TITLES] && (
                  <PillButton
                    active={orgActiveTab === 'team'}
                    onClick={() => setOrgActiveTab('team')}
                    label={t('organizations:publicView.ourTeam')}
                    icon="briefcase"
                  />
                )}
              </div>
              {orgActiveTab === 'events' && (
                <OrgEventsList
                  groupId={organizationId}
                  organization={{
                    name,
                    badgeImageUrl: badgeUrl,
                    id: organizationId
                  }}
                  user={user}
                />
              )}
              {orgActiveTab === 'team' && (
                <TeamList organizationId={organizationId} user={user} />
              )}
            </div>
          </CenteredContainer>
        )}
      <div className="relative z-10 border-b-1 md:mx-auto md:max-w-[800px] h-[39px] md:h-[84px]" />
    </>
  );
};

export default DesktopView;
